import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';

/**
 * Depth layers behind the dashboard: three soft light sources and a faint
 * grid, each moving at a different fraction of the scroll distance so the
 * foreground cards read as travelling over a deeper, slower ground.
 *
 * Kept deliberately plain — gradients and a lattice, no illustrative motifs —
 * so it stays out of the way of the NeevCloud mark and works at a glance.
 *
 * Everything here is `pointer-events-none` and `aria-hidden` — it is pure
 * atmosphere and must never intercept a click or reach a screen reader. It
 * also flattens entirely under prefers-reduced-motion.
 */
export function ParallaxBackground() {
  const reduce = useReducedMotion();
  const { scrollY } = useScroll();

  const slow = useTransform(scrollY, [0, 2400], [0, -110]);
  const drift = useTransform(scrollY, [0, 2400], [0, 150]);
  const fast = useTransform(scrollY, [0, 2400], [0, -420]);

  if (reduce) {
    return (
      <div aria-hidden="true" className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-40 -left-32 h-[38rem] w-[38rem] rounded-full bg-orchid-500/10 blur-[120px]" />
        <div className="absolute top-1/3 -right-40 h-[34rem] w-[34rem] rounded-full bg-teal-400/8 blur-[130px]" />
      </div>
    );
  }

  return (
    <div aria-hidden="true" className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
      <motion.div
        style={{ y: slow }}
        className="absolute -top-48 -left-40 h-[42rem] w-[42rem] rounded-full bg-orchid-500/14 blur-[130px]"
      />
      <motion.div
        style={{ y: drift }}
        className="absolute top-1/4 -right-52 h-[38rem] w-[38rem] rounded-full bg-teal-400/10 blur-[140px]"
      />
      <motion.div
        style={{ y: slow }}
        className="absolute bottom-0 left-1/3 h-[30rem] w-[30rem] rounded-full bg-lotus-500/10 blur-[130px]"
      />

      {/* Nearest: a faint grid that gives the parallax something crisp to
          register against, fading out toward the bottom. */}
      <motion.div style={{ y: fast }} className="absolute inset-0 opacity-[0.045]">
        <div
          className="h-[200%] w-full"
          style={{
            backgroundImage:
              'linear-gradient(to right, var(--grid-line) 1px, transparent 1px), linear-gradient(to bottom, var(--grid-line) 1px, transparent 1px)',
            backgroundSize: '72px 72px',
            maskImage: 'radial-gradient(ellipse 80% 55% at 50% 0%, black 20%, transparent 75%)',
            WebkitMaskImage:
              'radial-gradient(ellipse 80% 55% at 50% 0%, black 20%, transparent 75%)',
          }}
        />
      </motion.div>
    </div>
  );
}
